import { CouponV2 } from '@citruscamps/citrus-client'
import React from 'react'
import { useFetchMembershipPackages } from '../../hooks/useFetchMemberPackages'
import { useFetchInfiniteProductsV2 } from '../../pages/Financials/pages/CouponCreate/hooks/useFetchProductsV2'
import { useFetchInfiniteProductCategories } from '../../pages/Financials/pages/OrderDetails/components/AddOrderItemWizard/hooks/useFetchProductCategories'
import { formatList, pluralize } from '../../utils/formatters'
import { TruncatedText } from '../TruncatedText/TruncatedText'

interface IProps {
  coupon: CouponV2
  show?: 'all' | 'in-line' | 'amount' | 'restrictions'
  className?: string
}

export const CouponDescriptionV2 = ({ coupon, show = 'all', className }: IProps) => {
  const { program_id: programId } = coupon
  const { data: products } = useFetchInfiniteProductsV2({
    programId,
    fetchProps: {
      sort: 'created',
      order: 'DESC',
      query: {
        id: coupon.applies_to.products,
      },
    },
    isEnabled: (coupon.applies_to.products || []).length > 0,
    fetchAll: true,
  })
  const { data: productCategories } = useFetchInfiniteProductCategories({
    programId,
    fetchProps: {
      sort: 'created',
      order: 'DESC',
      query: {
        id: coupon.applies_to.product_categories,
      },
    },
    isEnabled: (coupon.applies_to.product_categories || []).length > 0,
    fetchAll: true,
  })
  const { data: packages } = useFetchMembershipPackages({
    programId,
    isEnabled: (coupon.restrictions.membership_packages || []).length > 0,
    fetchProps: {
      sort: 'created',
      order: 'DESC',
      query: {
        id: coupon.restrictions.membership_packages,
      },
    },
  })

  if (!coupon.amount_off?.major_value && !coupon.percent_off) {
    return null
  }
  const productList = formatList(
    products.map((p) => p.name).filter((c, i, l) => l.indexOf(c) === i),
  )
  const categoryList = formatList(
    productCategories.map((c) => c.name).filter((c, i, l) => l.indexOf(c) === i),
  )
  const packageList = formatList(
    packages.map((p) => p.name).filter((c, i, l) => l.indexOf(c) === i),
  )
  const restrictions = [
    coupon.restrictions.limit_customer && 'One use per customer',
    coupon.restrictions.max_redemptions &&
      `Limited to ${coupon.restrictions.max_redemptions} total redemptions`,
    coupon.restrictions.membership_packages &&
      coupon.restrictions.membership_packages?.length > 0 &&
      `Customers with ${packageList || 'specific memberships'}`,
    coupon.restrictions.minimum_items &&
      `Minimum ${coupon.restrictions.minimum_items} item${pluralize(
        coupon.restrictions.minimum_items,
      )} on order`,
    coupon.restrictions.minimum_amount?.major_value &&
      `Minimum ${coupon.restrictions.minimum_amount?.display} on order`,
    coupon.apply_automatically && 'Applies automatically',
  ]
    .filter(Boolean)
    .join(' • ')

  const amount = `${
    coupon.amount_off?.major_value ? coupon.amount_off.display : coupon.percent_off + '%'
  } ${
    !!coupon.applies_to.products && coupon.applies_to.products?.length > 0
      ? `off of ${productList}`
      : !!coupon.applies_to.product_categories && coupon.applies_to.product_categories?.length > 0
      ? `off of ${categoryList}`
      : 'off of all products'
  }`

  if (show === 'amount') {
    return <TruncatedText className={className}>{amount}</TruncatedText>
  }

  if (show === 'restrictions') {
    return <TruncatedText className={className}>{restrictions || 'No restrictions'}</TruncatedText>
  }

  if (show === 'in-line') {
    return (
      <TruncatedText className={className}>
        {amount}
        {' • '}
        {restrictions || 'No restrictions'}
      </TruncatedText>
    )
  }

  return (
    <>
      <TruncatedText className={className}>{amount}</TruncatedText>
      <TruncatedText className={className}>{restrictions || 'No restrictions'}</TruncatedText>
    </>
  )
}
